import type { SupabaseClient } from '@supabase/supabase-js';
import type { RunSummary } from '../core/run';
import { applyRunToCounters, detectAchievements, emptyCounters, type MetaCounters } from '../core/meta/achievements';
import type { Content } from '../data/schema';
import { ensureSession } from './supabase';
import { NET_TIMEOUT_MS, withTimeout } from './timeout';

export interface Meta {
  reflets: number;
  ogrines: number;
  talents: Record<string, number>;
  unlocks: string[];
  achievements: { id: string; at: string }[];
  counters: MetaCounters;
  activeModifiers: string[];
  refletsSpent: number;
  ogrinesSpent: number;
}

/**
 * Changements locaux pas encore fusionnés côté serveur.
 * `activeModifiers` vaut `null` tant que le joueur n'y a pas touché : le serveur remplace la liste quand il reçoit un tableau,
 * un tableau vide enverrait donc un « tout désactiver » à un appareil qui n'a rien changé.
 */
export interface MetaDelta {
  refletsDelta: number;
  ogrinesDelta: number;
  talents: Record<string, number>;
  replaceTalents: boolean;
  unlocks: string[];
  achievements: { id: string; at: string }[];
  counters: Partial<MetaCounters>;
  activeModifiers: string[] | null;
  refletsSpent: number;
  ogrinesSpent: number;
}

export interface SaveRunResult { meta: Meta; newAchievements: string[]; ogrinesEarned: number; }
/**
 * Le store est la seule source de vérité de la méta : toute modification passe par `update` (ou `saveRun`), calculée sur sa
 * méta courante. Les écritures locales sont immédiates ; la synchronisation (runs en file puis fusion de la méta) part en
 * arrière-plan et `subscribe` prévient de chaque nouvelle méta (commit local ou fusion distante).
 */
export interface ProgressStore {
  /** Synchronise (adoption ou fusion) puis renvoie la méta courante. */
  load(): Promise<Meta>;
  /** Enregistre la run en local (Reflets, succès, Ogrines, compteurs, file) et rend la main sans attendre le réseau. */
  saveRun(summary: RunSummary): Promise<SaveRunResult>;
  /** Applique `apply` à la méta courante du store (`null` : aucun changement) ; le delta se calcule contre cette méta. */
  update(apply: (meta: Meta) => Meta | null): Promise<Meta>;
  /** Compatibilité : remplace la méta par `meta` (préférer `update`, aucun appelant du client ne l'utilise). */
  saveMeta(meta: Meta): Promise<Meta>;
  /** Attend la synchronisation en cours (et celles qu'elle a déclenchées) ; résolue tout de suite s'il n'y en a pas. */
  sync(): Promise<void>;
  /** Abonnement aux nouvelles métas ; renvoie la fonction de désabonnement. */
  subscribe(listener: (meta: Meta) => void): () => void;
  current(): Meta;
}
export interface RemoteApi {
  userId(): Promise<string | null>;
  mergeMeta(userId: string, delta: MetaDelta): Promise<Meta | null>;
  insertRun(userId: string, summary: RunSummary): Promise<void>;
}
export interface KeyValue { get(k: string): string | null; set(k: string, v: string): void; }

const K_META = 'rogfus.meta', K_DELTA = 'rogfus.metaDelta', K_PENDING = 'rogfus.pendingRuns';
/** Utilisateur distant dont la méta locale est le reflet (écrit après chaque fusion réussie) ; marque M1 d'une méta locale non envoyée. */
const K_OWNER = 'rogfus.metaOwner', K_DIRTY = 'rogfus.metaDirty';
const COUNTER_KEYS = Object.keys(emptyCounters()) as (keyof MetaCounters)[];

export function emptyMeta(): Meta {
  return { reflets: 0, ogrines: 0, talents: {}, unlocks: [], achievements: [], counters: emptyCounters(), activeModifiers: [], refletsSpent: 0, ogrinesSpent: 0 };
}

export function emptyDelta(): MetaDelta {
  return { refletsDelta: 0, ogrinesDelta: 0, talents: {}, replaceTalents: false, unlocks: [], achievements: [], counters: {}, activeModifiers: null, refletsSpent: 0, ogrinesSpent: 0 };
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const nonNegInt = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0);
const uniq = <T>(xs: readonly T[]): T[] => [...new Set(xs)];
const isoNow = (): string => new Date().toISOString();
/** Ids connus de `table` (clé propre uniquement : `constructor` ou `__proto__` venus d'une sauvegarde sont rejetés), sans doublon. */
function knownIds(raw: unknown, table: Record<string, unknown>): string[] {
  if (!Array.isArray(raw)) return [];
  return uniq(raw.filter((id): id is string => typeof id === 'string' && Object.hasOwn(table, id)));
}

/** Complète les champs manquants (méta M1 incluse), borne les nombres et retire tout ce que le contenu ne connaît pas ; une date de succès absente vaut `now()`. */
export function normalizeMeta(raw: unknown, content: Content, now: () => string = isoNow): Meta {
  if (!isRecord(raw)) return emptyMeta();
  const talents: Record<string, number> = {};
  if (isRecord(raw.talents)) {
    for (const [id, v] of Object.entries(raw.talents)) {
      if (!Object.hasOwn(content.talents, id) || typeof v !== 'number' || !Number.isFinite(v)) continue;
      const rank = Math.min(Math.max(0, Math.floor(v)), content.talents[id]!.maxRank);
      if (rank >= 1) talents[id] = rank;
    }
  }
  const achievements: { id: string; at: string }[] = [];
  if (Array.isArray(raw.achievements)) {
    const seen = new Set<string>();
    for (const a of raw.achievements) {
      if (!isRecord(a) || typeof a.id !== 'string' || !Object.hasOwn(content.achievements, a.id) || seen.has(a.id)) continue;
      seen.add(a.id);
      achievements.push({ id: a.id, at: typeof a.at === 'string' && a.at !== '' ? a.at : now() });
    }
  }
  const counters = emptyCounters();
  if (isRecord(raw.counters)) for (const k of COUNTER_KEYS) counters[k] = nonNegInt(raw.counters[k]);
  return {
    reflets: nonNegInt(raw.reflets),
    ogrines: nonNegInt(raw.ogrines),
    talents,
    unlocks: knownIds(raw.unlocks, content.unlockables),
    achievements,
    counters,
    activeModifiers: knownIds(raw.activeModifiers, content.modifiers),
    refletsSpent: nonNegInt(raw.refletsSpent),
    ogrinesSpent: nonNegInt(raw.ogrinesSpent),
  };
}

/** Relit un delta enregistré : champs invalides ramenés à leur valeur vide plutôt que d'envoyer n'importe quoi au serveur. */
function sanitizeDelta(raw: unknown): MetaDelta {
  const d = emptyDelta();
  if (!isRecord(raw)) return d;
  const int = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : 0);
  const strings = (v: unknown): string[] => (Array.isArray(v) ? uniq(v.filter((x): x is string => typeof x === 'string')) : []);
  d.refletsDelta = int(raw.refletsDelta);
  d.ogrinesDelta = int(raw.ogrinesDelta);
  if (isRecord(raw.talents)) for (const [id, v] of Object.entries(raw.talents)) if (typeof v === 'number' && Number.isFinite(v) && v >= 0) d.talents[id] = Math.floor(v);
  d.replaceTalents = raw.replaceTalents === true;
  d.unlocks = strings(raw.unlocks);
  if (Array.isArray(raw.achievements)) d.achievements = raw.achievements.filter((a): a is { id: string; at: string } => isRecord(a) && typeof a.id === 'string' && typeof a.at === 'string');
  if (isRecord(raw.counters)) for (const k of COUNTER_KEYS) { const v = raw.counters[k]; if (typeof v === 'number' && Number.isFinite(v)) d.counters[k] = v; }
  d.activeModifiers = Array.isArray(raw.activeModifiers) ? strings(raw.activeModifiers) : null;
  d.refletsSpent = nonNegInt(raw.refletsSpent);
  d.ogrinesSpent = nonNegInt(raw.ogrinesSpent);
  return d;
}

function addCounters(base: Partial<MetaCounters>, to: MetaCounters, from: MetaCounters): Partial<MetaCounters> {
  const out = { ...base };
  for (const k of COUNTER_KEYS) { const inc = to[k] - from[k]; if (inc > 0) out[k] = (out[k] ?? 0) + inc; }
  return out;
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);
const maxByKey = (a: Record<string, number>, b: Record<string, number>): Record<string, number> => {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = Math.max(out[k] ?? 0, v);
  return out;
};
const unionAchievements = (a: readonly { id: string; at: string }[], b: readonly { id: string; at: string }[]): { id: string; at: string }[] => {
  const seen = new Set(a.map((x) => x.id));
  return [...a, ...b.filter((x) => !seen.has(x.id) && (seen.add(x.id), true))];
};

/** `a` puis `b` en un seul delta (même résultat serveur que deux fusions successives). `b.talents` non vide est la carte complète la plus récente. */
function combineDeltas(a: MetaDelta, b: MetaDelta): MetaDelta {
  const counters: Partial<MetaCounters> = { ...a.counters };
  for (const [k, v] of Object.entries(b.counters) as [keyof MetaCounters, number][]) counters[k] = (counters[k] ?? 0) + v;
  const bTalents = b.replaceTalents || Object.keys(b.talents).length > 0;
  return {
    refletsDelta: a.refletsDelta + b.refletsDelta,
    ogrinesDelta: a.ogrinesDelta + b.ogrinesDelta,
    talents: bTalents ? b.talents : a.talents,
    replaceTalents: a.replaceTalents || b.replaceTalents,
    unlocks: uniq([...a.unlocks, ...b.unlocks]),
    achievements: unionAchievements(a.achievements, b.achievements),
    counters,
    activeModifiers: b.activeModifiers ?? a.activeModifiers,
    refletsSpent: Math.max(a.refletsSpent, b.refletsSpent),
    ogrinesSpent: Math.max(a.ogrinesSpent, b.ogrinesSpent),
  };
}

/** Applique un delta à une méta comme le fait `rogfus_merge_meta` côté serveur (rebase des changements faits pendant un envoi). */
function applyDelta(m: Meta, d: MetaDelta): Meta {
  const counters = { ...m.counters };
  for (const [k, v] of Object.entries(d.counters) as [keyof MetaCounters, number][]) counters[k] = Math.max(0, counters[k] + v);
  return {
    reflets: Math.max(0, m.reflets + d.refletsDelta),
    ogrines: Math.max(0, m.ogrines + d.ogrinesDelta),
    talents: d.replaceTalents ? { ...d.talents } : maxByKey(m.talents, d.talents),
    unlocks: uniq([...m.unlocks, ...d.unlocks]),
    achievements: unionAchievements(m.achievements, d.achievements),
    counters,
    activeModifiers: d.activeModifiers ?? m.activeModifiers,
    refletsSpent: Math.max(m.refletsSpent, d.refletsSpent),
    ogrinesSpent: Math.max(m.ogrinesSpent, d.ogrinesSpent),
  };
}

/**
 * Delta d'adoption : fait entrer la méta locale dans une ligne serveur qui ne la connaît pas (autre utilisateur, ligne neuve,
 * méta M1 non envoyée). Les quantités prennent l'écart local - serveur, ou le delta en attente s'il est plus grand (gains hors
 * ligne d'un appareil neuf), jamais négatives ; talents par maximum, listes par union, dépenses par maximum.
 */
function adoptionDelta(local: Meta, pending: MetaDelta, server: Meta): MetaDelta {
  const gap = (l: number, s: number, p: number): number => Math.max(0, l - s, p);
  const counters: Partial<MetaCounters> = {};
  for (const k of COUNTER_KEYS) { const v = gap(local.counters[k], server.counters[k], pending.counters[k] ?? 0); if (v > 0) counters[k] = v; }
  return {
    refletsDelta: gap(local.reflets, server.reflets, pending.refletsDelta),
    ogrinesDelta: gap(local.ogrines, server.ogrines, pending.ogrinesDelta),
    talents: { ...local.talents },
    // une réinitialisation en attente reste une réinitialisation ; sinon fusion par maximum
    replaceTalents: pending.replaceTalents,
    unlocks: [...local.unlocks],
    achievements: [...local.achievements],
    counters,
    activeModifiers: [...local.activeModifiers],
    refletsSpent: local.refletsSpent,
    ogrinesSpent: local.ogrinesSpent,
  };
}

/** Erreurs de `insertRun` qui retirent la run de la file : 23505 doublon (déjà enregistrée), 23502/23514 ligne refusée définitivement. */
const DROP_CODES = new Set(['23505', '23502', '23514']);

export function createProgressStore(remote: RemoteApi | null, kv: KeyValue, content: Content, now: () => string = isoNow): ProgressStore {
  const parse = (key: string): unknown => { try { return JSON.parse(kv.get(key) ?? 'null'); } catch { return null; } };
  const save = (key: string, value: unknown): void => { try { kv.set(key, JSON.stringify(value)); } catch { /* persistance indisponible : l'état en mémoire reste la référence */ } };
  const setRaw = (key: string, value: string): void => { try { kv.set(key, value); } catch { /* idem */ } };
  const readRaw = (key: string): string | null => { try { return kv.get(key); } catch { return null; } };
  const norm = (raw: unknown): Meta => normalizeMeta(raw, content, now);

  // État en mémoire (le stockage peut refuser les écritures) recopié dans le stockage à chaque changement.
  let meta = norm(parse(K_META));
  let delta = sanitizeDelta(parse(K_DELTA));
  /** Delta parti au serveur et pas encore confirmé : gardé dans le stockage (avec `delta`) si la page se ferme pendant l'envoi. */
  let inflight: MetaDelta | null = null;
  let pending: RunSummary[] = (() => { const p = parse(K_PENDING); return Array.isArray(p) ? (p as RunSummary[]) : []; })();
  let owner = readRaw(K_OWNER);
  let dirty = readRaw(K_DIRTY) === '1';
  const listeners = new Set<(m: Meta) => void>();

  const persist = (): void => { save(K_META, meta); save(K_DELTA, inflight ? combineDeltas(inflight, delta) : delta); };
  const setPending = (p: RunSummary[]): void => { pending = p; save(K_PENDING, p); };
  const current = (): Meta => norm(meta);
  const notify = (): void => { for (const l of [...listeners]) { try { l(current()); } catch (e) { console.warn('abonné de la méta en erreur', e); } } };

  /** Envoie les runs en file une par une ; une erreur réseau arrête l'envoi (la run reste en file) sans bloquer la fusion de méta. */
  async function flushRuns(userId: string): Promise<void> {
    for (const s of [...pending]) {
      try {
        await remote!.insertRun(userId, s);
      } catch (err) {
        const code = (err as { code?: string } | null)?.code;
        if (code === undefined || !DROP_CODES.has(code)) return;
        if (code !== '23505') console.warn('Run refusée par la base, retirée de la file :', err);
      }
      // retrait par identité : une run ajoutée pendant l'envoi reste en file
      setPending(pending.filter((x) => x !== s));
    }
  }

  /** Fusion de `sent` ; en cas d'échec, `sent` revient devant les changements arrivés pendant l'envoi. */
  async function send(uid: string, sent: MetaDelta, restore: MetaDelta): Promise<Meta | null> {
    inflight = restore;
    delta = emptyDelta();
    persist();
    let merged: Meta | null;
    try { merged = await remote!.mergeMeta(uid, sent); } catch { merged = null; }
    inflight = null;
    if (!merged) { delta = combineDeltas(restore, delta); persist(); return null; }
    return merged;
  }

  /** Un passage de synchronisation ; `true` si la fusion a réussi. Ne lève jamais : un échec laisse file, delta et méta locale en l'état. */
  async function syncOnce(): Promise<boolean> {
    if (!remote) return false;
    let uid: string | null;
    try { uid = await remote.userId(); } catch { uid = null; }
    if (!uid) return false;
    try { await flushRuns(uid); } catch { /* file conservée */ }
    let merged: Meta | null;
    if (owner !== uid || dirty) {
      // adoption : la ligne serveur ne reflète pas cette méta locale, on la lit (delta vide) puis on y fait entrer la méta locale
      let server: Meta | null;
      try { server = await remote.mergeMeta(uid, emptyDelta()); } catch { server = null; }
      if (!server) return false;
      const before = delta;
      merged = await send(uid, adoptionDelta(meta, delta, norm(server)), before);
    } else {
      const sent = delta;
      merged = await send(uid, sent, sent);
    }
    if (!merged) return false;
    meta = norm(applyDelta(norm(merged), delta));
    owner = uid; setRaw(K_OWNER, uid);
    if (dirty) { dirty = false; setRaw(K_DIRTY, '0'); }
    persist();
    notify();
    return true;
  }

  // Une seule synchronisation à la fois ; un changement pendant l'envoi en relance une à la fin (si la précédente a réussi).
  let syncing: Promise<void> | null = null;
  let again = false;
  function requestSync(): Promise<void> {
    if (!remote) return Promise.resolve();
    if (syncing) { again = true; return syncing; }
    syncing = (async () => {
      let ok: boolean;
      do { again = false; ok = await syncOnce(); } while (ok && again);
    })().finally(() => { syncing = null; });
    return syncing;
  }

  function commitMeta(next: Meta): Meta {
    const m = norm(next);
    const prev = meta;
    const known = new Set(prev.achievements.map((a) => a.id));
    delta = {
      ...delta,
      refletsDelta: delta.refletsDelta + (m.reflets - prev.reflets),
      ogrinesDelta: delta.ogrinesDelta + (m.ogrines - prev.ogrines),
      talents: m.talents,
      // un rang qui baisse ou un talent qui disparaît (reset) : le serveur doit remplacer (et non fusionner par maximum), sinon il les garderait
      replaceTalents: delta.replaceTalents || Object.entries(prev.talents).some(([id, rank]) => (Object.hasOwn(m.talents, id) ? m.talents[id]! : 0) < rank),
      unlocks: uniq([...delta.unlocks, ...m.unlocks]),
      achievements: [...delta.achievements, ...m.achievements.filter((a) => !known.has(a.id))],
      counters: addCounters(delta.counters, m.counters, prev.counters),
      activeModifiers: sameList(m.activeModifiers, prev.activeModifiers) ? delta.activeModifiers : m.activeModifiers,
      refletsSpent: Math.max(delta.refletsSpent, m.refletsSpent),
      ogrinesSpent: Math.max(delta.ogrinesSpent, m.ogrinesSpent),
    };
    meta = m;
    persist();
    notify();
    void requestSync();
    return current();
  }

  // Les écritures locales sont synchrones (donc atomiques) : aucune ne peut s'intercaler entre la lecture de la méta et son commit.
  return {
    current,
    sync: () => syncing ?? Promise.resolve(),
    subscribe: (listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    load: async () => { await requestSync(); return current(); },
    update: (apply) => {
      const next = apply(current());
      return Promise.resolve(next ? commitMeta(next) : current());
    },
    saveMeta: (next) => Promise.resolve(commitMeta(next)),
    saveRun: (summary) => {
      // run d'entraînement (salle, seed ou étage forcés par l'URL) : aucune progression
      if (summary.practice) return Promise.resolve({ meta: current(), newAchievements: [], ogrinesEarned: 0 });
      const counters = applyRunToCounters(meta.counters, summary);
      const newAchievements = detectAchievements(content.achievements, summary, counters, meta.achievements.map((a) => a.id));
      const at = now();
      const earned = newAchievements.map((id) => ({ id, at }));
      const ogrinesEarned = newAchievements.reduce((sum, id) => sum + content.achievements[id]!.ogrines, 0);
      const unlocked = newAchievements.flatMap((id) => { const u = content.achievements[id]!.unlocks; return u !== undefined && Object.hasOwn(content.unlockables, u) ? [u] : []; });
      delta = {
        ...delta,
        refletsDelta: delta.refletsDelta + summary.refletsEarned,
        ogrinesDelta: delta.ogrinesDelta + ogrinesEarned,
        achievements: [...delta.achievements, ...earned],
        unlocks: uniq([...delta.unlocks, ...unlocked]),
        counters: addCounters(delta.counters, counters, meta.counters),
      };
      meta = {
        ...meta,
        reflets: meta.reflets + summary.refletsEarned,
        ogrines: meta.ogrines + ogrinesEarned,
        counters,
        achievements: [...meta.achievements, ...earned],
        unlocks: uniq([...meta.unlocks, ...unlocked]),
      };
      persist();
      setPending([...pending, summary]);
      notify();
      void requestSync();
      return Promise.resolve({ meta: current(), newAchievements, ogrinesEarned });
    },
  };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v: unknown): v is string => typeof v === 'string' && UUID.test(v);

/** Chaque appel est borné à `NET_TIMEOUT_MS` : un dépassement est un échec réseau (file et delta conservés par le store). */
export function createSupabaseRemote(client: SupabaseClient): RemoteApi {
  return {
    userId: () => withTimeout(ensureSession(client), NET_TIMEOUT_MS, 'session'),
    async mergeMeta(_userId, d) {
      const { data, error } = await withTimeout(Promise.resolve(client.rpc('rogfus_merge_meta', {
        p_reflets_delta: d.refletsDelta,
        p_ogrines_delta: d.ogrinesDelta,
        p_talents: d.talents,
        p_replace_talents: d.replaceTalents,
        p_unlocks: d.unlocks,
        p_achievements: d.achievements,
        p_counters: d.counters,
        p_active_modifiers: d.activeModifiers,
        p_reflets_spent: d.refletsSpent,
        p_ogrines_spent: d.ogrinesSpent,
      })), NET_TIMEOUT_MS, 'rogfus_merge_meta');
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null | undefined;
      if (!row) return null;
      // Ligne brute : le store la normalise (ids inconnus, bornes) avant de la garder.
      return {
        reflets: row.reflets, ogrines: row.ogrines, talents: row.talents, unlocks: row.unlocks, achievements: row.achievements,
        counters: row.counters, activeModifiers: row.active_modifiers, refletsSpent: row.reflets_spent, ogrinesSpent: row.ogrines_spent,
      } as Meta;
    },
    async insertRun(userId, s) {
      const { error } = await withTimeout(Promise.resolve(client.from('rogfus_runs').insert({
        user_id: userId,
        client_id: isUuid(s.clientId) ? s.clientId : null,
        seed: s.seed,
        class_id: s.classId,
        // compat file M0 : une ligne mise en file avant M1 porte `roomId` au lieu de `floorId`
        room_id: s.floorId ?? (s as { roomId?: string }).roomId ?? 'astrub',
        outcome: s.outcome,
        duration_ms: s.durationMs,
        kills: s.kills,
        kamas: s.kamas,
        level: s.level,
        reflets_earned: s.refletsEarned,
        score: s.score ?? 0,
        rooms_cleared: s.roomsCleared ?? 0,
        // classement du jour : seulement avec la seed du serveur (une seed locale de repli ne se compare pas aux autres)
        daily_date: s.dailySource === 'server' && typeof s.dailyDay === 'string' ? s.dailyDay : null,
        summary: { stats: s.stats, talentsOwned: s.talentsOwned, petId: s.petId },
      })), NET_TIMEOUT_MS, 'rogfus_runs');
      if (error) throw error;
    },
  };
}
